import { type FC, type ReactElement, type ReactNode, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { userPermissionsSelector, userSelector } from 'store/auth/auth.selectors';
import { activePathSelector } from 'store/sidemenu/sidemenu.selectors';
import { SET_ACTIVE_PATH } from 'store/sidemenu/sidemenu.action';
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import MenuButton from './MenuBtn';
import { ProgramCodes, SpecialPermission } from 'common/enum';
import { useTheme } from '@/hooks/ThemeContext';
import {
  ArrowLeftRight,
  Building2,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  CircleHelp,
  FilePlus,
  Folder,
  LayoutDashboard,
  LogOut,
  Moon,
  Sun,
  User,
} from 'lucide-react';

const appTitle = 'SC Healthcare Coalition Portal';
const appVersion = `Version ${__APP_VERSION__}`;

type defaultType = {
  key: number | string;
  name: string;
  path: string;
  startIcon?: string | ReactNode;
  endIcon?: string | ReactNode;
};

type MenuList = {
  key: number | string;
  name: string | ReactElement;
  path: string;
  startIcon?: string | ReactNode;
  endIcon?: string | ReactNode;
  subMenu?: defaultType[];
}[];

/*
 * DrawerMenu Footer — Dropdown Structure:
 *
 * ┌──────────────────────┐
 * │ ┌──┐                  │
 * │ │PA│ Phil Armijo    ⌃⌄ │  ← Avatar trigger
 * │ └──┘ parm@earth...    │
 * └──────────────────────┘
 *            │ click
 *            ▼
 * ┌──────────────────────┐
 * │ ⓘ Contact Support    │
 * │ ☀ Light Mode         │  ← does not close dropdown
 * │───────────────────── │
 * │ → Log out            │
 * │───────────────────── │
 * │   Version X.Y.Z      │
 * └──────────────────────┘
 */

const DrawerMenu: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const userAccount = useAppSelector(userSelector);
  const permissions = useAppSelector(userPermissionsSelector);
  const activePath = useAppSelector(activePathSelector);
  const { isDarkMode, toggleTheme } = useTheme();
  const isAdmin = userAccount?.pvAdministrator && !!userAccount.pvAdministrator;
  const userFullName = userAccount
    ? `${userAccount?.pvPersonGivenName} ${userAccount?.pvPersonSurName}`
    : 'User Name';
  const userEmail = userAccount?.email ?? '';
  const avatarInitials = userAccount
    ? `${userAccount.pvPersonGivenName?.charAt(0) ?? ''}${userAccount.pvPersonSurName?.charAt(0) ?? ''}`
    : '';
  const [isOpen, setIsOpen] = useState(false);
  const [activeSubMenu, setActiveSubMenu] = useState(false);

  const menuItems: MenuList = useMemo(() => {
    const menuOrderByKey: Record<string, number> = {
      dashboard: 1,
      organization: 2,
      'resource-request': 3,
      'patient-transfer': 4,
      users: 5,
      archive: 6,
      'file-library': 7,
    };
    const menu: MenuList = [
      {
        key: 'dashboard',
        name: 'Dashboard',
        path: '/dashboard',
        startIcon: <LayoutDashboard />,
      },
      {
        key: 'organization',
        name: 'Organization',
        path: '/dashboard/organizations',
        startIcon: <Building2 />,
      },
      // TODO: Archive menu item -- uncomment when feature is ready
      // Requires: import { FileArchive } from 'lucide-react'
    ];
    if (permissions) {
      const resRequestEnabled = permissions.find((p) => p.code === ProgramCodes.RESOURCE_REQUEST);
      const patientTransferEnabled = permissions.find(
        (p) => p.code === ProgramCodes.PATIENT_TRANSFER
      );
      const fileLibraryEnabled = permissions.find((p) => p.code === ProgramCodes.FILE_LIBRARY);
      if (
        resRequestEnabled &&
        (resRequestEnabled?.permission === SpecialPermission.COORDINATOR ||
          resRequestEnabled?.permission === SpecialPermission.ADMINISTRATOR)
      ) {
        menu.push({
          key: 'resource-request',
          name: 'Resource request',
          path: '/dashboard/resource-request',
          startIcon: <FilePlus />,
        });
      }
      if (
        patientTransferEnabled &&
        (patientTransferEnabled?.permission === SpecialPermission.HOSPITAL ||
          patientTransferEnabled?.permission === SpecialPermission.READ_ONLY)
      ) {
        menu.push({
          key: 'patient-transfer',
          name: 'Patient transfer',
          path: '/dashboard/patient-transfer',
          startIcon: <ArrowLeftRight />,
        });
      }
      if (fileLibraryEnabled && fileLibraryEnabled?.active && fileLibraryEnabled?.hasPermission) {
        menu.push({
          key: 'file-library',
          name: 'File Library',
          path: '/dashboard/file-library',
          startIcon: <Folder />,
        });
      }
    }
    if (isAdmin) {
      menu.push({
        key: 'users',
        name: 'Users',
        path: '/dashboard/users',
        startIcon: <User />,
      });
    }
    menu.sort((a, b) => {
      const aOrder = menuOrderByKey[a.key] || 100;
      const bOrder = menuOrderByKey[b.key] || 100;
      return aOrder - bOrder;
    });
    return menu;
  }, [permissions, isAdmin]);

  useEffect(() => {
    const handleToggle = () => {
      setIsOpen((prevState) => !prevState);
    };
    window.addEventListener('toggle-sidebar', handleToggle);
    return () => window.removeEventListener('toggle-sidebar', handleToggle);
  }, []);

  function findValueByKey(arr: MenuList, pathToFind: string): string | ReactElement | undefined {
    for (const obj of arr) {
      if (obj.path === pathToFind) {
        return obj.name;
      }

      if (obj.subMenu && obj.subMenu.length) {
        const nestedResult = findValueByKey(obj.subMenu, pathToFind);
        if (nestedResult !== undefined) {
          return nestedResult;
        }
      }
    }
    return undefined;
  }

  const handleLogout = () => {
    const themePreference = localStorage.getItem('theme-preference');
    sessionStorage.clear();
    localStorage.clear();
    if (themePreference) {
      localStorage.setItem('theme-preference', themePreference);
    }
    window.location.href = '/';
  };

  const handleContactSupport = () => {
    setIsOpen(false);
    window.dispatchEvent(new CustomEvent('open-contact-support'));
  };

  const onMenuClick = (link: string) => {
    dispatch(SET_ACTIVE_PATH(link));
    const menu = menuItems.find((item) => item.path === link);
    if (activeSubMenu) {
      const subMenu = findValueByKey(menuItems, link);
      if (subMenu && !menu) {
        setActiveSubMenu((prevState) => !prevState);
        setIsOpen(false);
      }
    }
    if (menu) {
      if (menu.subMenu && menu.subMenu.length) {
        setActiveSubMenu((prevState) => !prevState);
      } else {
        setIsOpen(false);
      }
    }
    if (!link.includes('/')) return null;
    if (link === '/dashboard/agencies') {
      setIsOpen(false);
    }
    navigate(link);
    setIsOpen(false);
  };

  const onOpenChange = (open: boolean) => {
    setIsOpen(open);
  };

  return (
    <Drawer direction={'left'} open={isOpen} onOpenChange={onOpenChange}>
      <DrawerContent className={'hc:bg-background hc:overflow-hidden hc:border-none'}>
        <DrawerHeader>
          <div className={'hc:flex hc:flex-col hc:items-center'}>
            <img
              src={'/images/login-logo.png'}
              alt={'logo'}
              className={'hc:object-contain hc:mix-blend-multiply hc:dark:mix-blend-normal'}
            />
          </div>
          <DrawerTitle className={'hc:w-full hc:text-center'}>{appTitle}</DrawerTitle>
          {/* UPPER CONTENT */}
          <div className={'hc:flex hc:flex-col hc:mt-6 hc:w-full hc:relative hc:gap-1.5'}>
            <p
              className={
                'hc:text-typography-secondary hc:dark:text-muted-foreground hc:text-xs hc:font-medium'
              }>
              Menu
            </p>
            {menuItems.length
              ? menuItems.map((item, idx) => {
                  const isActive = activePath === item.path;
                  const withEndIcon = item?.endIcon;
                  const withSubMenu = item?.subMenu && item?.subMenu.length;
                  const startIcon = isActive ? (
                    <span className={'hc:text-icons-avatar'}>{item?.startIcon}</span>
                  ) : (
                    <span className={'hc:flex hc:items-center hc:text-muted-foreground!'}>
                      {item?.startIcon}
                    </span>
                  );
                  return (
                    <div key={`menu_item_${item.key || idx}`} className={'hc:flex hc:w-full'}>
                      {activeSubMenu && !isActive ? null : (
                        <MenuButton
                          className={'hc:p-4'}
                          fullWidth={true}
                          startIcon={activeSubMenu && isActive ? <ChevronLeft /> : startIcon}
                          endIcon={
                            withSubMenu ? (
                              activeSubMenu && isActive ? null : (
                                <ChevronRight />
                              )
                            ) : withEndIcon ? (
                              item.endIcon
                            ) : null
                          }
                          onClick={() => onMenuClick(item.path)}>
                          {item.name}
                        </MenuButton>
                      )}

                      <div
                        className={
                          isActive && withSubMenu && activeSubMenu
                            ? 'hc:flex hc:overflow-hidden'
                            : 'hc:hidden'
                        }>
                        <div className={'hc:flex hc:flex-col hc:w-full hc:gap-1 hc:pl-4'}>
                          {item?.subMenu?.map((subItem) => {
                            const isActive = activePath.includes(String(subItem.key));
                            const startIcon = isActive ? (
                              subItem?.startIcon
                            ) : (
                              <span
                                style={{
                                  display: 'flex',
                                  color: '#C2C2C2',
                                  alignItems: 'center',
                                }}>
                                {subItem?.startIcon}
                              </span>
                            );
                            return (
                              <MenuButton
                                key={subItem.key}
                                className={'hc:px-3 hc:py-4'}
                                fullWidth={true}
                                onClick={() => onMenuClick(subItem.path)}
                                startIcon={subItem?.startIcon ? startIcon : null}
                                endIcon={subItem?.endIcon ? subItem.endIcon : null}>
                                {subItem.name}
                              </MenuButton>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })
              : null}
          </div>
        </DrawerHeader>
        <DrawerFooter>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                aria-label='User menu'
                className='hc:flex hc:w-full hc:items-center hc:gap-3 hc:rounded-lg hc:p-3 hc:text-left hc:hover:bg-accent hc:cursor-pointer'>
                <Avatar>
                  <AvatarFallback className='hc:bg-icons-avatar hc:text-white'>
                    {avatarInitials}
                  </AvatarFallback>
                </Avatar>
                <div className='hc:grid hc:flex-1 hc:text-sm hc:leading-tight'>
                  <span className='hc:truncate hc:font-medium hc:text-muted-foreground'>
                    {userFullName}
                  </span>
                  {userEmail && (
                    <span className='hc:truncate hc:text-xs hc:text-muted-foreground/50'>
                      {userEmail}
                    </span>
                  )}
                </div>
                <ChevronsUpDown className='hc:ml-auto hc:size-4 hc:text-typography-secondary hc:dark:text-muted-foreground/80' />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className='hc:w-(--radix-dropdown-menu-trigger-width) hc:min-w-56 hc:rounded-lg hc:bg-popover hc:text-muted-foreground hc:border-border'
              side='top'
              align='end'
              sideOffset={4}>
              <DropdownMenuGroup>
                <DropdownMenuItem className='hc:cursor-pointer' onSelect={handleContactSupport}>
                  <CircleHelp />
                  Contact Support
                </DropdownMenuItem>
                <DropdownMenuItem
                  className='hc:cursor-pointer'
                  onSelect={(e) => {
                    e.preventDefault();
                    toggleTheme();
                  }}>
                  {isDarkMode ? <Sun /> : <Moon />}
                  {isDarkMode ? 'Light Mode' : 'Dark Mode'}
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuItem className='hc:cursor-pointer' onSelect={handleLogout}>
                <LogOut />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <p className='hc:text-center hc:text-xs hc:font-normal hc:text-typography-tertiary hc:dark:text-foreground/80 hc:mt-1'>
            {appVersion}
          </p>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
export default DrawerMenu;
